import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Common';
import {
  BilledFilterControlled,
  ButtonColumn,
  DateFilterControlled,
  FilterContainer,
  ShipmentTypeFilterControlled,
  TankSelectorControlled,
} from '@components/Common/EntryFilters';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import { Columns } from '@components/Common/SharedStyles';
import { compareDatesAsc } from '@helpers/dateUtils';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useTanksQuery } from '@api/queries/tanks';
import {
  useApprovedEntriesQuery,
  entryStatus,
} from '@api/queries/inventory';
import {
  useBilledEntryIdsQuery,
  useMarkEntriesBilledMutation,
  useMarkEntriesNotBilledMutation,
} from '@api/queries/billing';
import { useBillingFilters } from '@hooks/useBillingFilters';
import Modes from '../Common/InventoryNavigation';
import BillingTable from './BillingTable';

const BillingList: React.FC = () => {
  const { t } = useTranslation();

  // Get filter state from URL search params
  const {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setDateFilter,
    setBilledFilter,
    setShipmentTypeFilter,
  } = useBillingFilters();

  // Fetch reference data
  const { data: terminals = [] } = useTerminalsQuery();
  const { data: tanks = [] } = useTanksQuery();

  // Fetch approved entries for selected tank
  const {
    data: entries = [],
    isLoading: isLoadingEntries,
    refetch: refetchEntries,
  } = useApprovedEntriesQuery(
    { tankId: filters.tankId || undefined },
    !!filters.tankId
  );

  // Fetch billed entry IDs
  const {
    data: billedEntryIds = new Set<string>(),
    isLoading: isLoadingBillings,
    refetch: refetchBillings,
  } = useBilledEntryIdsQuery();

  // Mutations
  const markBilledMutation = useMarkEntriesBilledMutation();
  const markNotBilledMutation = useMarkEntriesNotBilledMutation();

  // Derived state
  const selectedTank = useMemo(
    () => (filters.tankId ? tanks.find((tank) => tank.id === filters.tankId) : null),
    [tanks, filters.tankId]
  );

  const isLoading = isLoadingEntries || isLoadingBillings;

  // Filter entries based on current filters
  const filteredEntries = useMemo(() => {
    let filtered = entries.filter(
      (e) => e.tankId === filters.tankId && e.entryState === entryStatus.approved
    );

    if (filters.startDate) {
      filtered = filtered.filter((e) => compareDatesAsc(e.order, filters.startDate!) >= 0);
    }

    if (filters.endDate) {
      filtered = filtered.filter((e) => compareDatesAsc(e.order, filters.endDate!) <= 0);
    }

    if (typeof filters.isBilled === 'boolean') {
      filtered = filtered.filter((e) => billedEntryIds.has(e.id) === filters.isBilled);
    }

    if (typeof filters.shipmentType === 'number') {
      filtered = filtered.filter((e) => e.shipmentType === filters.shipmentType);
    }

    return filtered;
  }, [entries, filters, billedEntryIds]);

  // Handlers
  const handleRefresh = useCallback(() => {
    if (filters.tankId) {
      refetchEntries();
      refetchBillings();
    }
  }, [filters.tankId, refetchEntries, refetchBillings]);

  const handleMarkBilled = useCallback(() => {
    const entryIds = filteredEntries.map((e) => e.id);
    if (entryIds.length > 0) {
      markBilledMutation.mutate(entryIds);
    }
  }, [filteredEntries, markBilledMutation]);

  const handleMarkNotBilled = useCallback(() => {
    const entryIds = filteredEntries.map((e) => e.id);
    if (entryIds.length > 0) {
      markNotBilledMutation.mutate(entryIds);
    }
  }, [filteredEntries, markNotBilledMutation]);

  const isMutating = markBilledMutation.isPending || markNotBilledMutation.isPending;

  return (
    <>
      <Modes
        isEnabled={!!filters.tankId}
        fetch={handleRefresh}
        isLoading={isLoading}
      />
      <FilterContainer>
        <Columns>
          <TankSelectorControlled
            terminals={terminals}
            tanks={tanks}
            value={tankFilter}
            onChange={setTankFilter}
          />
          <BilledFilterControlled
            value={filters.isBilled}
            onChange={setBilledFilter}
          />
          <DateFilterControlled
            value={dateFilter}
            onChange={setDateFilter}
          />
          <ShipmentTypeFilterControlled
            value={filters.shipmentType}
            onChange={setShipmentTypeFilter}
          />
          <ButtonColumn className="column">
            <Button
              primary={true}
              onClick={handleMarkBilled}
              disabled={isMutating || filteredEntries.length === 0}
            >
              {t('billing.mark_entries_billed')}
            </Button>
            <Button
              secondary={true}
              onClick={handleMarkNotBilled}
              disabled={isMutating || filteredEntries.length === 0}
            >
              {t('billing.mark_entries_not_billed')}
            </Button>
          </ButtonColumn>
        </Columns>
      </FilterContainer>
      {selectedTank && (
        <ContentContainer>
          {isLoading && <ContentLoader />}
          <BillingTable entries={filteredEntries} billedEntryIds={billedEntryIds} />
        </ContentContainer>
      )}
    </>
  );
};

export default BillingList;
